import React, { useEffect, useRef, useState } from 'react';
import Icon from './Icon';
import { clears } from './useToaster';
import './Toaster.css';

const HOLD_MS = 5000;
const LEAVE_MS = 300;
const reducedMotion = () => typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * One toast (Bridge Toast 1452:479): the Banner without its leading icon.
 * Neutral and Success clear after five seconds; the hold pauses on hover and
 * on keyboard focus and restarts on the way out, so it never clears
 * mid-sentence (WCAG 2.2.1). Warning and Error stay. Every toast has Dismiss.
 */
function Toast({ toast, onDismiss }) {
  const [leaving, setLeaving] = useState(false);
  const hold = useRef({ left: HOLD_MS, since: 0, timer: null });

  const leave = () => {
    if (leaving) return;
    setLeaving(true);
    setTimeout(() => onDismiss(toast.id), reducedMotion() ? 0 : LEAVE_MS);
  };
  const run = () => {
    const h = hold.current;
    if (!clears(toast) || h.timer) return;
    h.since = Date.now();
    h.timer = setTimeout(() => { h.timer = null; leave(); }, h.left);
  };
  const pause = () => {
    const h = hold.current;
    if (!h.timer) return;
    clearTimeout(h.timer);
    h.timer = null;
    h.left = Math.max(0, h.left - (Date.now() - h.since));
  };

  useEffect(() => {
    const h = hold.current;
    run();
    return () => clearTimeout(h.timer);
    // Once, when it arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={`resui-toast resui-toast--${toast.tone}${leaving ? ' is-leaving' : ''}`}
      data-component="Toast"
      onMouseEnter={pause}
      onMouseLeave={run}
      onFocus={pause}
      onBlur={run}
    >
      <span className="resui-toast-message">{toast.message}</span>
      <button type="button" className="resui-toast-close" aria-label="Dismiss" onClick={leave}>
        <Icon name="x" size={20} />
      </button>
    </div>
  );
}

/**
 * The stack, top right, newest on top, right-aligned so toasts of different
 * widths share the right edge. It is a polite live region, so one that clears
 * on its own is still announced once (PRD-1702, ResUI Design Decisions, Toast).
 */
function Toaster({ toasts, onDismiss }) {
  return (
    <div className="resui-toaster" aria-live="polite" aria-atomic="false">
      {toasts.map((toast) => <Toast key={toast.id} toast={toast} onDismiss={onDismiss} />)}
    </div>
  );
}

export default Toaster;
